import type {
  Journal,
  JournalCharts,
  JournalChartData,
  JournalRecord,
  JournalId,
  JournalItem,
  JournalReports,
} from '@/types/journal';
import { EvaluationChartType } from '@/types/ratings';
import { toDiaryEvaluation } from '@/utils/diary';
import { compareLatvian, getFullName } from '@/utils/text';

export const getJournalKey = ({
  classId,
  disciplineId,
  groupIndex,
  joinedGroupId,
}: JournalId): string =>
  `${classId}-${disciplineId}-${groupIndex}-${joinedGroupId}`;

export const getTeacherNames = (journal: Journal): string =>
  (journal.journalTeachers ?? []).map(getFullName).join(', ');

export const sortJournals = (journals: Journal[]): Journal[] =>
  journals
    .map((journal) => ({
      ...journal,
      journalTeachers: [...(journal.journalTeachers ?? [])].sort(
        (teacher, otherTeacher) =>
          compareLatvian(getFullName(teacher), getFullName(otherTeacher)),
      ),
    }))
    .sort((journal, otherJournal) =>
      compareLatvian(journal.name, otherJournal.name),
    );

const journalChartsByType: Record<
  EvaluationChartType,
  keyof JournalCharts | null
> = {
  [EvaluationChartType.Numeric]: 'decimalChartData',
  [EvaluationChartType.Percent]: 'percentChartData',
  [EvaluationChartType.AchievableResult]: 'achievableResultChartData',
  [EvaluationChartType.Weighted]: null,
};

const JOURNAL_CHART_TYPES = [
  EvaluationChartType.Numeric,
  EvaluationChartType.Percent,
  EvaluationChartType.AchievableResult,
];

export const getJournalChart = (
  charts: JournalCharts | undefined,
  type: EvaluationChartType,
): JournalChartData | null => {
  const chartKey = journalChartsByType[type];
  const chart = chartKey ? charts?.[chartKey] : null;

  return chart?.chartItems?.length ? chart : null;
};

export const getJournalChartTypes = (
  charts: JournalCharts | undefined,
): EvaluationChartType[] =>
  JOURNAL_CHART_TYPES.filter((type) => !!getJournalChart(charts, type));

const formatJournalRecord = (item: JournalItem, index = 0): JournalRecord => {
  const lesson = item.record?.lesson ?? item.record?.specialLesson;
  const behaviourEntry = item.record?.behaviourEntry;

  return {
    id: `${index}-${item.date}`,
    date: item.date,
    lessonNumber: item.lessonNumber ?? null,
    subject: lesson?.subjectWithAttachments
      ? {
          ...lesson.subjectWithAttachments,
          attachments: lesson.subjectWithAttachments.attachments ?? [],
        }
      : null,
    homeTasks: (lesson?.homeTasks ?? []).map((task) => ({
      id: task.id,
      text: task.task?.text ?? '',
      attachments: task.task?.attachments ?? [],
      authorName: task.authorName,
      timeCreated: task.timeCreated,
      lastModification: task.lastModification,
      areAnswersEnabled: task.areAnswersEnabled,
      hasStudentAnswer: task.hasStudentAnswer,
    })),
    evaluation: lesson?.evaluation
      ? toDiaryEvaluation(lesson.evaluation)
      : null,
    behaviourEntry: behaviourEntry
      ? {
          subject: behaviourEntry.subject,
          authorName: behaviourEntry.authorName,
          text: behaviourEntry.notes,
          timeCreated: behaviourEntry.dateCreated,
          type: behaviourEntry.type,
        }
      : null,
  };
};

export const formatJournalRecords = (items: JournalItem[]): JournalRecord[] =>
  items.map(formatJournalRecord);

export const hasJournalItemContent = (item: JournalItem): boolean => {
  const { subject, homeTasks, evaluation, behaviourEntry } =
    formatJournalRecord(item);

  return (
    !!subject?.text ||
    !!subject?.attachments?.length ||
    homeTasks.length > 0 ||
    !!evaluation ||
    !!behaviourEntry
  );
};

export const isEmptyReport = (reports: JournalReports): boolean =>
  !reports.journalItems?.length &&
  getJournalChartTypes(reports.charts).length === 0 &&
  !reports.nonAttendancesByType?.attendableLessonsCount;
